import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

/*
  Every share card the product has ever produced came out of this file, and
  until now it said the wrong thing about the wrong site. It emitted two
  `og:title` tags — the real one, then a hardcoded "astring - Anonymous Chat
  Application" — and scrapers take the last, so the product branded itself as
  the exact category it exists to not be. `og:url` pointed at astring.com; the
  product is on astring.app (FooterSection.tsx:33,37,55). There was no image, no
  locale, no card type and no canonical, so a link posted to the Instagram and
  Facebook accounts the footer drives to rendered as a blank box.
*/

/** The product lives here. Not astring.com — that was never ours. */
const SITE_URL = "https://astring.app";

/** The 1200x630 link-preview card (public/og.png). */
const OG_IMAGE = { path: "/og.png", width: "1200", height: "630" };

/*
  OpenGraph wants a full locale, not a language tag. `mn` is the default the app
  boots into (i18n/index.ts), so it is also the default here — a scraper that
  gets no answer should get the one most visitors see.
*/
const OG_LOCALE = { mn: "mn_MN", en: "en_US" } as const;

const SEO = ({
  title,
  description,
  noindex,
}: {
  title: string;
  description: string;
  noindex?: boolean;
}) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  /*
    index.html carries a static copy of these tags, because no scraper runs the
    JavaScript that produces the ones below (see the comment there). A real
    browser gets both, and two `og:title` tags is the bug this file was fixing —
    so the fallbacks are dropped the moment the live ones exist. Helmet does not
    manage them, so it will not do this for us.
  */
  useEffect(() => {
    document
      .querySelectorAll("head [data-seo-fallback]")
      .forEach((tag) => tag.remove());
  }, []);

  // Defensive: several test suites stub react-i18next down to `t` alone.
  const lang = i18n?.language?.startsWith("en") ? "en" : "mn";
  const other = lang === "en" ? "mn" : "en";
  const canonical = `${SITE_URL}${pathname}`;

  /*
    The locale is held in localStorage, not in the URL, so both hreflang
    alternates point at the same canonical address — that address genuinely
    serves both languages. When per-locale URLs exist they belong here.
  */
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex" />}

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang={lang} href={canonical} />
      <link rel="alternate" hrefLang={other} href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:site_name" content="aSTRING" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      <meta property="og:locale:alternate" content={OG_LOCALE[other]} />
      <meta property="og:image" content={`${SITE_URL}${OG_IMAGE.path}`} />
      <meta property="og:image:width" content={OG_IMAGE.width} />
      <meta property="og:image:height" content={OG_IMAGE.height} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${SITE_URL}${OG_IMAGE.path}`} />
    </Helmet>
  );
};

export default SEO;
